import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { acceptPendingInvite, declinePendingInvite, fetchPendingInvites, switchBiz } from '../../api';
import { PendingInvite } from '../../types';
import { AuthModel } from './core/_models';
import { useAuth } from './core/Auth';
import { getUserByToken } from './core/_requests';

// Global banner for invites created by the pending-invite flow (spec-pending-invite-flow.md).
// Mounted once in MasterLayout, which only renders on authenticated routes.
export const PendingInvitesBanner: React.FC = () => {
  const { currentUser, saveAuth, logout } = useAuth();
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [busyBizId, setBusyBizId] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) return;
    fetchPendingInvites()
      .then((result) => setInvites(result || []))
      .catch(() => {
        // Silent — a failed pending-invites check should never block the app shell.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email]);

  const handleAccept = async (invite: PendingInvite) => {
    setBusyBizId(invite.bizId);
    try {
      await acceptPendingInvite(invite.bizId);
      setInvites((prev) => prev.filter((i) => i.bizId !== invite.bizId));
      toast.success(`${'Invite accepted! Switching to'.toTranslatedDisplay()} ${invite.bizName}…`);

      const result = await switchBiz(invite.bizId);
      if (result?.token) {
        const auth: AuthModel = { api_token: result.token, refreshToken: undefined };
        logout();
        saveAuth(auth);
        await getUserByToken(auth.api_token);
        window.location.reload();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to accept invite.'.toTranslatedDisplay());
    } finally {
      setBusyBizId(null);
    }
  };

  const handleDecline = async (invite: PendingInvite) => {
    setBusyBizId(invite.bizId);
    try {
      await declinePendingInvite(invite.bizId);
      setInvites((prev) => prev.filter((i) => i.bizId !== invite.bizId));
      toast.success('Invite declined.'.toTranslatedDisplay());
    } catch (error: any) {
      toast.error(error.message || 'Failed to decline invite.'.toTranslatedDisplay());
    } finally {
      setBusyBizId(null);
    }
  };

  if (invites.length === 0) return null;

  return (
    <div
      className="position-fixed top-0 start-50 translate-middle-x mt-3 px-3"
      style={{ zIndex: 1080, width: 'min(560px, 92vw)' }}
    >
      {invites.map((invite) => (
        <div
          key={invite.bizId}
          className="alert alert-info d-flex align-items-center justify-content-between shadow-sm mb-2 py-3"
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-envelope-paper-fill fs-4 me-3 text-info"></i>
            <span className="text-gray-800">
              <strong className="text-gray-900">{invite.bizName}</strong> {'invited you to join as'.toTranslatedDisplay()}{' '}
              <strong className="text-gray-900">{invite.role}</strong>
            </span>
          </div>
          <div className="d-flex gap-2 ms-3 flex-shrink-0">
            <button
              className="btn btn-sm btn-info"
              disabled={busyBizId === invite.bizId}
              onClick={() => handleAccept(invite)}
            >
              {'Accept'.toTranslatedDisplay()}
            </button>
            <button
              className="btn btn-sm btn-light"
              disabled={busyBizId === invite.bizId}
              onClick={() => handleDecline(invite)}
            >
              {'Decline'.toTranslatedDisplay()}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
